/**
 * Desktop article DOM helpers
 * @module lib/desktopArticle
 */

const { logWarn } = require('./logger');
const { applySlotTargeting } = require('./gptConfig');

const TABLET_STICKY_RENDER_FAILSAFE_MS = 4500;

function isTinyCreativeSize(size) {
  if (!size || !Array.isArray(size) || size.length < 2) return false;
  const width = Number(size[0]);
  const height = Number(size[1]);
  return Number.isFinite(width) && Number.isFinite(height) && width <= 1 && height <= 1;
}

function hasRenderedAdChild(slot) {
  if (!slot || !slot.childNodes) return false;
  return Array.prototype.some.call(slot.childNodes, (node) => {
    if (!node || node.nodeType !== 1) return false;
    return !node.classList || !node.classList.contains('milla-ad-placeholder');
  });
}

function reserveCollapsedTabletStickyAd(stickyContainer, adContainer) {
  if (!stickyContainer || !stickyContainer.style) return;
  stickyContainer.style.visibility = 'hidden';
  stickyContainer.style.height = '0px';
  stickyContainer.style.minHeight = '0px';
  stickyContainer.style.padding = '0';
  stickyContainer.style.overflow = 'hidden';
  stickyContainer.style.boxShadow = 'none';
  stickyContainer.style.pointerEvents = 'none';
  if (adContainer && adContainer.style) {
    adContainer.style.height = '0px';
    adContainer.style.minHeight = '0px';
    adContainer.style.overflow = 'hidden';
  }
}

function collapseTabletStickyAd(stickyContainer, adContainer) {
  reserveCollapsedTabletStickyAd(stickyContainer, adContainer);
  if (stickyContainer && stickyContainer.style) {
    stickyContainer.style.display = 'none';
    if (typeof stickyContainer.setAttribute === 'function') {
      stickyContainer.setAttribute('aria-hidden', 'true');
    }
  }
  if (adContainer && adContainer.style) {
    adContainer.style.display = 'none';
  }
}

function restoreTabletStickyAd(stickyContainer, adContainer) {
  if (!stickyContainer || !stickyContainer.style) return;
  stickyContainer.style.display = '';
  stickyContainer.style.visibility = '';
  stickyContainer.style.height = '';
  stickyContainer.style.minHeight = '';
  stickyContainer.style.padding = '8px 0';
  stickyContainer.style.overflow = '';
  stickyContainer.style.boxShadow = '0 -2px 10px rgba(0,0,0,0.1)';
  stickyContainer.style.pointerEvents = '';
  if (typeof stickyContainer.removeAttribute === 'function') {
    stickyContainer.removeAttribute('aria-hidden');
  }
  if (adContainer && adContainer.style) {
    adContainer.style.display = 'inline-block';
    adContainer.style.height = '';
    adContainer.style.minHeight = '';
    adContainer.style.overflow = '';
  }
}

/**
 * Returns whether the publisher-provided top ad wrapper should be hidden for
 * the current ad context.
 */
function shouldHideTopAdWrapper(_noads, pro) {
  return _noads === 'yes' || pro === 'active';
}

/**
 * Hides the publisher-provided top ad wrapper now and, when the document is
 * still loading, once more after the remaining template markup is available.
 */
function hideTopAdWrapper(runtimeDocument = typeof document !== 'undefined' ? document : null) {
  if (!runtimeDocument || typeof runtimeDocument.querySelector !== 'function') {
    return false;
  }

  const hide = () => {
    const topAdWrapper = runtimeDocument.querySelector('.top-ad-wrapper');
    if (!topAdWrapper || !topAdWrapper.style) return false;
    topAdWrapper.style.display = 'none';
    return true;
  };

  const hidden = hide();
  if (
    runtimeDocument.readyState === 'loading'
    && typeof runtimeDocument.addEventListener === 'function'
  ) {
    runtimeDocument.addEventListener('DOMContentLoaded', hide, { once: true });
  }
  return hidden;
}

function findStickyAncestor(node) {
  if (typeof window === 'undefined' || typeof window.getComputedStyle !== 'function') {
    return null;
  }
  let current = node ? node.parentElement : null;
  while (current && current !== document.body && current !== document.documentElement) {
    if (window.getComputedStyle(current).position === 'sticky') return current;
    current = current.parentElement;
  }
  return null;
}

/**
 * Keeps a template top-ad wrapper that leads a sticky header (RebelMouse puts
 * it first in `.main header`, which is `position: sticky; top: 0`) from
 * sticking: the header's `top` follows minus the wrapper height, so the ad
 * scrolls away and only the bar below it sticks. Moving the wrapper out of the
 * header instead moves `.main`'s top edge 250px after first paint, which Chrome
 * scores as a full-viewport layout shift.
 * @returns {boolean} false when the wrapper is not at the top of a sticky
 *   ancestor and still has to be moved out of it
 */
function unstickTopAdWrapper(topAdWrapper) {
  const stickyAncestor = findStickyAncestor(topAdWrapper);
  if (!stickyAncestor) return true;
  const leadsStickyAncestor = () => (
    topAdWrapper.offsetParent === stickyAncestor && topAdWrapper.offsetTop <= 0
  );
  // A hidden wrapper has no offsetParent; it is measured once it shows.
  if (topAdWrapper.offsetParent && !leadsStickyAncestor()) return false;
  if (topAdWrapper.dataset.millaUnstuck === 'true') return true;
  topAdWrapper.dataset.millaUnstuck = 'true';

  const baseTop = parseFloat(window.getComputedStyle(stickyAncestor).top) || 0;
  const syncTop = () => {
    const wrapperHeight = leadsStickyAncestor() ? topAdWrapper.offsetHeight : 0;
    stickyAncestor.style.top = `${baseTop - wrapperHeight}px`;
  };
  syncTop();
  if (typeof ResizeObserver !== 'undefined') {
    new ResizeObserver(syncTop).observe(topAdWrapper);
  }
  return true;
}

/**
 * Injects top ad and skin ad containers into the page if they don't exist.
 * Used by desktop article and homepage factories.
 */
function injectTopAndSkinAdContainers() {
  if (typeof document === 'undefined') {
    return;
  }

  let topAdWrapper = document.querySelector('.top-ad-wrapper');
  let topAdSlot = document.getElementById('top-ad');
  const templateWrapper = Boolean(topAdWrapper);

  if (!topAdWrapper) {
    topAdWrapper = document.createElement('div');
    topAdWrapper.className = 'top-ad-wrapper';
    topAdWrapper.style.height = 'auto';
    topAdWrapper.style.position = 'relative';

    const topAdFlex = document.createElement('div');
    topAdFlex.className = 'top-ad-flex';

    topAdSlot = document.createElement('div');
    topAdSlot.id = 'top-ad';
    topAdSlot.className = 'ad-slot';

    topAdFlex.appendChild(topAdSlot);
    topAdWrapper.appendChild(topAdFlex);

    if (document.body) {
      document.body.insertBefore(topAdWrapper, document.body.firstChild);
    }
  } else if (!topAdSlot) {
    topAdSlot = document.createElement('div');
    topAdSlot.id = 'top-ad';
    topAdSlot.className = 'ad-slot';
    let flex = topAdWrapper.querySelector('.top-ad-flex');
    if (!flex) {
      flex = document.createElement('div');
      flex.className = 'top-ad-flex';
      topAdWrapper.appendChild(flex);
    }
    flex.appendChild(topAdSlot);
  }

  // A template wrapper stays where the page painted it; only a wrapper that
  // cannot be kept from sticking moves to the top of the body.
  const keptInPlace = templateWrapper && unstickTopAdWrapper(topAdWrapper);
  if (!keptInPlace && document.body && topAdWrapper && document.body.firstChild !== topAdWrapper) {
    document.body.insertBefore(topAdWrapper, document.body.firstChild);
  }

  let skinAd = document.getElementById('skin-ad');
  if (!skinAd) {
    skinAd = document.createElement('div');
    skinAd.id = 'skin-ad';
    skinAd.className = 'ad-slot';
    if (topAdWrapper && topAdWrapper.parentElement) {
      topAdWrapper.parentElement.insertBefore(
        skinAd,
        topAdWrapper.nextSibling
      );
    } else if (document.body) {
      document.body.appendChild(skinAd);
    }
  }

  try {
    if (
      typeof window !== 'undefined' &&
      window.sessionStorage &&
      window.sessionStorage.getItem('hideTopAd') === 'true' &&
      topAdWrapper
    ) {
      topAdWrapper.style.display = 'none';
    }
  } catch (err) {
    logWarn('Failed to apply hideTopAd from sessionStorage', err);
  }
}

/**
 * Creates and initializes a tablet sticky bottom ad (769-1024px viewport).
 * Used by desktop article pages.
 * @param {object} googletag - Google Publisher Tag object
 * @param {string} gamPath - GAM ad unit path (e.g., '/1020360/custom-header-narcity')
 */
function setupTabletStickyAd(googletag, gamPath) {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0;
  if (viewportWidth <= 768 || viewportWidth > 1024) {
    return;
  }

  // Create sticky container
  const stickyContainer = document.createElement('div');
  stickyContainer.id = 'milla-tablet-sticky-bottom';
  stickyContainer.style.cssText = `
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    z-index: 9999;
    background: #fff;
    box-shadow: 0 -2px 10px rgba(0,0,0,0.1);
    text-align: center;
    padding: 8px 0;
  `;

  // Create ad container
  const adContainer = document.createElement('div');
  adContainer.id = 'tablet-sticky-ad';
  adContainer.style.cssText = 'display: inline-block; margin: 0 auto;';
  stickyContainer.appendChild(adContainer);

  // Create close button
  const closeBtn = document.createElement('button');
  closeBtn.innerHTML = '&times;';
  closeBtn.style.cssText = `
    position: absolute;
    top: -12px;
    right: 10px;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    border: 1px solid #ccc;
    background: #fff;
    font-size: 16px;
    line-height: 1;
    cursor: pointer;
    z-index: 10000;
  `;
  closeBtn.onclick = function () {
    stickyContainer.style.display = 'none';
  };
  stickyContainer.appendChild(closeBtn);

  document.body.appendChild(stickyContainer);
  reserveCollapsedTabletStickyAd(stickyContainer, adContainer);

  // Define and display the ad slot with custom refresh logic
  googletag.cmd.push(function () {
    const stickySlot = applySlotTargeting(
      googletag.defineSlot(gamPath, [[728, 90], [320, 50], [300, 50]], 'tablet-sticky-ad'),
      {
        lazyload: 'lazyload',
        refresh: 'true'
      }
    ).addService(googletag.pubads());

    let stickyRefreshTimer = null;
    let stickyRenderFailsafeTimer = null;
    let stickyIsEmpty = true;
    const EMPTY_REFRESH_MS = 5000;
    const FILLED_REFRESH_MS = 30000;

    function clearStickyRenderFailsafe() {
      if (!stickyRenderFailsafeTimer) return;
      clearTimeout(stickyRenderFailsafeTimer);
      stickyRenderFailsafeTimer = null;
    }

    function scheduleStickyRenderFailsafe() {
      clearStickyRenderFailsafe();
      stickyRenderFailsafeTimer = setTimeout(function () {
        if (stickyContainer.style.display === 'none') {
          return;
        }
        if (hasRenderedAdChild(adContainer)) {
          stickyIsEmpty = false;
          restoreTabletStickyAd(stickyContainer, adContainer);
          scheduleStickyRefresh();
          return;
        }
        stickyIsEmpty = true;
        collapseTabletStickyAd(stickyContainer, adContainer);
      }, TABLET_STICKY_RENDER_FAILSAFE_MS);
    }

    function refreshStickyAd() {
      if (stickyContainer.style.display === 'none') {
        return;
      }
      scheduleStickyRenderFailsafe();
      googletag.pubads().refresh([stickySlot]);
    }

    function scheduleStickyRefresh() {
      if (stickyRefreshTimer) {
        clearTimeout(stickyRefreshTimer);
      }
      const delay = stickyIsEmpty ? EMPTY_REFRESH_MS : FILLED_REFRESH_MS;
      stickyRefreshTimer = setTimeout(function () {
        refreshStickyAd();
      }, delay);
    }

    // Listen for render events on this slot
    googletag.pubads().addEventListener('slotRenderEnded', function (event) {
      if (event.slot === stickySlot) {
        clearStickyRenderFailsafe();
        if (event.isEmpty || isTinyCreativeSize(event.size)) {
          stickyIsEmpty = true;
          collapseTabletStickyAd(stickyContainer, adContainer);
          return;
        }
        stickyIsEmpty = false;
        restoreTabletStickyAd(stickyContainer, adContainer);
        scheduleStickyRefresh();
      }
    });

    googletag.display('tablet-sticky-ad');
    googletag.pubads().refresh([stickySlot]);
  });
}

/**
 * Whether the viewport is wide enough for the skin creative to draw, per the
 * brand's `desktopSkin.minViewportWidth`. Brands without the setting keep the
 * skin.
 */
function isSkinAdEligible(config, viewportWidth) {
  const minViewportWidth = Number(config && config.desktopSkin && config.desktopSkin.minViewportWidth);
  if (!Number.isFinite(minViewportWidth)) return true;
  const width = Number(viewportWidth);
  return Number.isFinite(width) && width >= minViewportWidth;
}

/**
 * Keeps the skin slot out of the page flow while its request is in flight.
 * GPT sizes the skin container to 2000x1200 at request time, and Narcity's
 * `#skin-ad { height: 0 }` rule only arrives with the non-critical stylesheet.
 */
function prepareSkinAdSlot(slot) {
  if (!slot || !slot.style) return;
  slot.style.height = '0px';
}

/**
 * Applies a skin slot's render result. GPT's ON_NO_FILL collapse does not
 * always run for the skin, and an uncollapsed empty skin keeps its 2000x1200
 * GPT container in the page flow as a blank block.
 * @returns {boolean} true when the empty slot was collapsed
 */
function applySkinAdRenderResult(slot, event) {
  if (!slot || !slot.style || !event) return false;
  if (event.isEmpty) {
    slot.style.display = 'none';
    slot.style.height = '0px';
    return true;
  }
  slot.style.display = '';
  return false;
}

module.exports = {
  applySkinAdRenderResult,
  hideTopAdWrapper,
  injectTopAndSkinAdContainers,
  isSkinAdEligible,
  prepareSkinAdSlot,
  shouldHideTopAdWrapper,
  setupTabletStickyAd
};
